import { SLICE_TAG } from '@/constants';

import { apiSlice } from '../api-slice';
import { setCustomerSlice } from './customer-slice';
import {
    ResponseCustomerListSchema,
    ResponseSingleCustomerSchema,
    SingleCustomer,
} from './customer.schema';

export const customerApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        createCustomer: builder.mutation({
            query: (data) => ({
                url: 'customers',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: SLICE_TAG.CUSTOMERS, id },
                { type: SLICE_TAG.CUSTOMERS },
            ],
        }),

        getCustomers: builder.query({
            query: (searchParams) => {
                return {
                    url: `customers`,
                    method: 'GET',
                    params: searchParams,
                };
            },
            transformResponse: (response) => {
                return ResponseCustomerListSchema.parse(response);
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setCustomerSlice(data));
                } catch (error) {
                    console.error('Failed to fetch CUSTOMER:', error);
                }
            },
            keepUnusedDataFor: 600,

            providesTags: [SLICE_TAG.CUSTOMERS],
        }),

        // for fetching single customer details
        // kept for legacy reasons
        getSingleCustomer: builder.query<SingleCustomer['data'], string>({
            query: (id) => `customers/${id}`,
            // providesTags: ['Single_Customer'],
            providesTags: (_result, _error, id) => [
                { type: SLICE_TAG.CUSTOMERS, id },
            ],
            keepUnusedDataFor: 600,
            transformResponse: (response) => {
                return ResponseSingleCustomerSchema.parse(response).data;
            },
        }),

        // new hook
        getCustomerById: builder.query<SingleCustomer['data'], string>({
            query: (id) => `customers/${id}`,
            providesTags: (_result, _error, id) => [
                { type: SLICE_TAG.CUSTOMERS, id },
            ],
            keepUnusedDataFor: 600,
            transformResponse: (response) => {
                return ResponseSingleCustomerSchema.parse(response).data;
            },
        }),

        updateCustomerById: builder.mutation({
            query: (payload) => {
                const { id, ...rest } = payload;
                return {
                    url: `customers/${id}`,
                    method: 'PUT',
                    body: rest,
                };
            },
            invalidatesTags: (_result, _error, id) => [
                { type: SLICE_TAG.CUSTOMERS, id },
                { type: SLICE_TAG.CUSTOMERS },
            ],
        }),

        deleteCustomerById: builder.mutation({
            query: (id) => ({
                url: `customers/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: SLICE_TAG.CUSTOMERS, id },
                { type: SLICE_TAG.CUSTOMERS },
            ],
        }),

        addCustomerPayment: builder.mutation({
            query: (payload) => {
                const { id, ...rest } = payload;
                return {
                    url: `customers/${id}/pay`,
                    method: 'PUT',
                    body: {
                        amount: rest.amount,
                        payment_method: rest.payment_method,
                        transaction_id: rest.transaction_id,
                    },
                };
            },
            invalidatesTags: (_result, _error, id) => [
                { type: SLICE_TAG.CUSTOMERS, id },
                { type: SLICE_TAG.CUSTOMERS },
            ],
        }),
    }),
});

export const {
    useCreateCustomerMutation,
    useLazyGetCustomersQuery,
    useGetCustomersQuery,
    useLazyGetSingleCustomerQuery,
    useUpdateCustomerByIdMutation,
    useDeleteCustomerByIdMutation,
    useGetCustomerByIdQuery,
    useGetSingleCustomerQuery,
    useLazyGetCustomerByIdQuery,
    useAddCustomerPaymentMutation,
} = customerApi;
